import { useState, useEffect, useContext } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm } from 'Hooks/useForm';
import { KYBFirstStep } from './KYBFirstStep';
import { KYBSecondStep } from './KYBSecondStep';
import { KYBThirdStep } from './KYBThirdStep';
import { KYBFourthStep } from './KYBFourthStep';
import { KYBFifthStep } from './KYBFifthStep';
import { KYBSixthStep } from './KYBSixthStep';
import { KYBSeventhStep } from './KYBSeventhStep';
import { KYBSucccess } from './KYBSuccess';
import { AuthContext } from 'context/auth-context';
import { useTheme } from '@mui/material/styles';
import { KYB as APIKYB } from 'lib/api';
import useMediaQuery from '@mui/material/useMediaQuery';

import * as dayjs from 'dayjs';

const useStyles = makeStyles({
    dialog: {
        zIndex: 2
    },
    banner: {
        backgroundColor: '#182D55 !important',
        maxWidth: '16em',
        minHeight: '100%',
        paddingTop: '3em'
    },
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%'
    },
    bannerTitle: {
        color: 'white',
        fontWeight: '700!important'
    },
    bannerSpan: {
        color: 'white'
    },
    bannerText: {
        margin: '1em'
    },
    dialogContent: {
        backgroundColor: 'transparent!important',
        padding: '0!important'
    },
    content: {
        margin: '2em',
        width: '100%'
    },
    contentTitle: {
        fontWeight: '700!important'
    },
    verifyOption: {
        borderRadius: '5px',
        border: '1px solid #d4d4d4',
        padding: '1em',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
    },
    verifyText: {
        maxWidth: '30em'
    },
    verifyTitle: {
        fontWeight: '700!important'
    },
    startKYX: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 3em!important'
    },
    doItLater: {
        marginTop: '10em'
    }
});

export const KYBModal = ({ modalKYB, handleModal }) => {
    const [activePage, setActivePage] = useState(3);
    const classes = useStyles();
    const [formValues, handleInputChange, setFormValues] = useForm({
        id: 0,
        id_entity_type: 0,
        naics_code: 0,
        legal_name: '',
        description: '',
        phone_number: '',
        website_url: '',
        ein: '',
        formation_date: null,
        city: '',
        line1: '',
        line2: '',
        state: '',
        country: 'US',
        postalCode: ''
    });
    const [ownersArray, setOwnersArray] = useState([]);
    const [ownOwner, setOwnOwner] = useState(null);
    const [radioOwner, setRadioOwner] = useState(true);
    const [entities, setEntities] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [isSignButtonDisabled, setIsSignButtonDisabled] = useState(false);
    const { businessInfo, userInfo } = useContext(AuthContext);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    // General config effect
    useEffect(() => {
        let isMounted = true;
        APIKYB.getEntities().then(response => {
            if (!isMounted) return;
            setEntities(response.rows);
        });
        APIKYB.getIndustries().then(response => {
            if (!isMounted) return;
            setIndustries(response.rows);
        });
        return () => {
            isMounted = false;
        };
    }, []);
    // Owners use effect
    useEffect(() => {
        if (businessInfo?.owners?.length) {
            const copyOwners = [];
            let hasOwnOwner = false;
            for (let i = 0; i < businessInfo.owners.length; i += 1) {
                const owner = businessInfo?.owners[i];
                const isOwn = userInfo.id === owner.user?.id;
                if (isOwn) {
                    setOwnOwner(owner);
                    hasOwnOwner = true;
                    continue;
                }
                copyOwners.push({
                    completed: true,
                    isValid: true,
                    id: owner.id,
                    title: owner.title,
                    ownership: owner.ownership,
                    first_name: owner.first_name,
                    last_name: owner.last_name,
                    phone_number: owner.phone_number?.replace('+1', ''),
                    email: owner.email,
                    date_of_birth: dayjs(owner.date_of_birth).format('YYYY-MM-DD'),
                    ssn: owner.ssn,
                    bus_line_1: owner.address?.line1,
                    bus_line_2: owner.address?.line2,
                    bus_city: owner.address?.city,
                    bus_state: owner.address?.state,
                    bus_PO: owner.address?.postalCode
                });
            }
            setOwnersArray(copyOwners);
            if (!hasOwnOwner) {
                setRadioOwner(false);
            }
            setActivePage(4);
            return;
        }
    }, [businessInfo.owners, userInfo]);
    // Banks use effect
    useEffect(() => {
        if (businessInfo.banks?.length) {
            setActivePage(7);
            return;
        }
    }, [businessInfo.banks]);

    useEffect(() => {
        if (!userInfo.kyb) {
            setActivePage(0);
            return;
        }
        if (!businessInfo.id) {
            setActivePage(0);
            return;
        }
        setFormValues({
            id: businessInfo.id,
            id_entity_type: businessInfo.id_entity_type,
            naics_code: businessInfo.naics_code,
            legal_name: businessInfo.legal_name,
            description: businessInfo.description,
            phone_number: businessInfo.phone_number?.replace('+1', ''),
            website_url: businessInfo.website_url,
            ein: businessInfo.ein,
            formation_date: dayjs(businessInfo.formation_date).toDate(),
            city: businessInfo.address.city,
            line1: businessInfo.address.line1,
            line2: businessInfo.address.line2,
            state: businessInfo.address.state,
            country: 'US',
            postalCode: businessInfo.address.postalCode
        });
        if (businessInfo.is_ownership_disclosure_signed) {
            setActivePage(6);
        } else if (businessInfo.owners.length > 0) {
            setActivePage(5);
        } else {
            setActivePage(4);
        }
    }, [setFormValues, userInfo.kyb, businessInfo]);

    const incrementPage = () => {
        setActivePage(activePage + 1);
    };

    const decreasePage = () => {
        setActivePage(activePage - 1);
    };

    return (
        <Dialog fullScreen={fullScreen} maxWidth="lg" className={classes.dialog} open={modalKYB}>
            <DialogContent
                className={classes.dialogContent}
                sx={{
                    minWidth: { lg: '64em', md: '50em', sm: 'auto', xs: 'auto' }
                }}>
                {{
                    0: (
                        <KYBFirstStep
                            handleInputChange={handleInputChange}
                            setFormValues={setFormValues}
                            formValues={formValues}
                            activePage={activePage}
                            handleClose={handleModal}
                            incrementPage={() => incrementPage()}
                        />
                    ),
                    1: (
                        <KYBSecondStep
                            handleInputChange={handleInputChange}
                            formValues={formValues}
                            activePage={activePage}
                            handleClose={handleModal}
                            incrementPage={() => incrementPage()}
                            decreasePage={() => decreasePage()}
                        />
                    ),
                    2: (
                        <KYBThirdStep
                            handleInputChange={handleInputChange}
                            formValues={formValues}
                            setFormValues={setFormValues}
                            activePage={activePage}
                            handleClose={handleModal}
                            entities={entities}
                            industries={industries}
                            incrementPage={() => incrementPage()}
                            decreasePage={() => decreasePage()}
                        />
                    ),
                    3: (
                        <KYBFourthStep
                            activePage={activePage}
                            handleClose={handleModal}
                            formValues={formValues}
                            incrementPage={() => incrementPage()}
                            decreasePage={() => decreasePage()}
                        />
                    ),
                    4: (
                        <KYBFifthStep
                            handleInputChange={handleInputChange}
                            formValues={formValues}
                            setFormValues={setFormValues}
                            activePage={activePage}
                            handleClose={handleModal}
                            radioOwner={radioOwner}
                            setRadioOwner={setRadioOwner}
                            ownersArray={ownersArray}
                            setOwnersArray={setOwnersArray}
                            ownOwner={ownOwner}
                            setOwnOwner={setOwnOwner}
                            incrementPage={() => incrementPage()}
                            decreasePage={() => decreasePage()}
                        />
                    ),
                    5: (
                        <KYBSixthStep
                            formValues={formValues}
                            activePage={activePage}
                            handleClose={handleModal}
                            isSignButtonDisabled={isSignButtonDisabled}
                            setIsSignButtonDisabled={setIsSignButtonDisabled}
                            incrementPage={() => incrementPage()}
                            decreasePage={() => decreasePage()}
                        />
                    ),
                    6: (
                        <KYBSeventhStep
                            handleInputChange={handleInputChange}
                            formValues={formValues}
                            activePage={activePage}
                            handleClose={handleModal}
                            incrementPage={() => incrementPage()}
                            decreasePage={() => decreasePage()}
                        />
                    ),
                    7: <KYBSucccess />
                }[activePage] || <div>error</div>}
            </DialogContent>
        </Dialog>
    );
};
